const BASE = process.env.REACT_APP_API_BASE_URL;

// Publishing fans out notification emails, so give write calls a generous
// ceiling — but never none at all. Without a deadline a stalled connection
// leaves the UI spinning on "Publishing…" forever with nothing to report.
const WRITE_TIMEOUT_MS = 60000;

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

/** Raised when the caller aborted a request on purpose (e.g. pressed Cancel). */
export class RequestAbortedError extends Error {
  constructor(message = 'Request cancelled') {
    super(message);
    this.name = 'RequestAbortedError';
    this.aborted = true;
  }
}

/**
 * fetch() with a timeout, caller-supplied abort support, and error messages a
 * user can act on.
 *
 * A bare fetch rejects with `TypeError: Failed to fetch` for every network-layer
 * problem — dropped connection, proxy timeout, CORS. Surfacing that string
 * verbatim is what made a backend timeout look like a frontend bug.
 */
async function requestWithTimeout(url, options = {}, { timeoutMs = WRITE_TIMEOUT_MS, signal } = {}) {
  const controller = new AbortController();
  const onAbort = () => controller.abort();

  if (signal) {
    if (signal.aborted) throw new RequestAbortedError();
    signal.addEventListener('abort', onAbort, { once: true });
  }

  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (timedOut) {
      throw new Error(
        'The server took too long to respond. Your post may still have been saved — ' +
          'refresh the feed before trying again.'
      );
    }
    if (err?.name === 'AbortError') throw new RequestAbortedError();
    if (err instanceof TypeError) {
      throw new Error(
        'Could not reach the server. Check your connection — if the post was already ' +
          'created, it will appear when you refresh.'
      );
    }
    throw err;
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onAbort);
  }
}

// Relative paths (/media/...) are served by Django; prefix them with the API base URL.
// Absolute URLs (S3 in production) pass through unchanged.
function resolveMediaUrl(url) {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BASE}${url}`;
}

const STATUS_MAP = { APPROVED: 'published', PENDING: 'pending', REJECTED: 'rejected' };

export function normalizePost(p) {
  return {
    id: p.id,
    author: {
      type: p.author_type === 'ADMIN' ? 'suspire' : 'brand',
      name: p.author_name || '',
      logo_url: resolveMediaUrl(p.brand_logo_url),
    },
    title: p.title || '',
    body: p.body || '',
    // Legacy posts predate the field and are plain text; default accordingly so
    // they keep rendering exactly as before.
    body_format: p.body_format === 'HTML' ? 'HTML' : 'TEXT',
    post_type: (p.post_type || '').toLowerCase(),
    attachment_url: resolveMediaUrl(p.attachment_url),
    attachment_name: p.attachment_name || '',
    attachment_type: p.attachment_type ? p.attachment_type.toUpperCase() : null,
    cta_url: p.cta_url || null,
    cta_label: p.cta_label || null,
    status: STATUS_MAP[p.approval_status] || 'published',
    reactions: p.reactions || { like: 0, acknowledge: 0, useful: 0, excited: 0 },
    user_reaction: p.user_reaction || null,
    created_at: p.published_at || p.created_at,
    visibility_gated: p.locked || false,
    gated_plan_name: p.available_on_plans?.length > 0 ? p.available_on_plans[0] : null,
    rejection_reason: p.rejection_reason || null,
  };
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export async function fetchFeed(token, { page = 1, pageSize = 20, postType = '', search = '' } = {}) {
  const params = new URLSearchParams({ page, page_size: pageSize });
  if (postType) params.set('post_type', postType.toUpperCase());
  if (search) params.set('search', search);
  const res = await fetch(`${BASE}/api/circle/posts/?${params}`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(`Failed to load feed (${res.status})`);
  const data = await res.json();
  return { count: data.count, hasMore: !!data.next, results: (data.results || []).map(normalizePost) };
}

/**
 * Load a single post by id.
 *
 * Backs the ?post=<id> deep link the notification email points at: the post may
 * sit on page 4 of the feed, or be plan-gated, so it can't be assumed present in
 * whatever the feed has already loaded.
 */
export async function fetchPost(token, postId) {
  const res = await fetch(`${BASE}/api/circle/posts/${postId}/`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(`Failed to load post (${res.status})`);
  return normalizePost(await res.json());
}

export async function fetchPostingEligibility(token) {
  try {
    const res = await fetch(`${BASE}/api/circle/me/posting-eligibility/`, {
      headers: authHeaders(token),
    });
    if (!res.ok) return { eligible: true, reason: '', current_plan: null };
    return res.json();
  } catch {
    return { eligible: true, reason: '', current_plan: null };
  }
}

export async function fetchMySubmissions(token) {
  try {
    const res = await fetch(`${BASE}/api/circle/me/submissions/`, {
      headers: authHeaders(token),
    });
    if (!res.ok) return { count: 0, results: [] };
    const data = await res.json();
    return {
      count: data.count,
      results: (data.results || []).map(p => ({
        ...normalizePost(p),
        status: STATUS_MAP[p.approval_status] || 'pending',
      })),
    };
  } catch {
    return { count: 0, results: [] };
  }
}

/**
 * Upload a post attachment.
 *
 * Returns `{ url, filename }`. The storage key prefixes the filename with a
 * uuid/timestamp for uniqueness, so the URL basename is not presentable — the
 * upload endpoint hands back the original name and callers persist it on the
 * post as `attachment_name`.
 */
export async function uploadAttachment(token, file, { signal } = {}) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('file_type', file.type.startsWith('image/') ? 'image' : 'document');
  const res = await requestWithTimeout(
    `${BASE}/api/core/s3/upload/`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    },
    { signal }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.error || `Attachment upload failed (${res.status})`);
  }
  const data = await res.json();
  // Dev returns a relative path (/media/...) — resolve to absolute so the
  // backend URLField validator accepts it. Production S3 URLs pass through unchanged.
  return {
    url: resolveMediaUrl(data.file_url),
    filename: data.filename || file.name || '',
  };
}

export async function fetchPendingPosts(token) {
  const res = await fetch(`${BASE}/api/circle/admin/posts/?status=PENDING`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(`Failed to load pending posts (${res.status})`);
  const data = await res.json();
  return {
    count: data.count,
    results: (data.results || []).map(p => ({ ...normalizePost(p), status: 'pending' })),
  };
}

export async function approvePost(token, postId) {
  const res = await fetch(`${BASE}/api/circle/admin/posts/${postId}/approve/`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(`Approve failed (${res.status})`);
  return res.json().catch(() => ({}));
}

export async function rejectPost(token, postId, reason) {
  const res = await fetch(`${BASE}/api/circle/admin/posts/${postId}/reject/`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ rejection_reason: reason }),
  });
  if (!res.ok) throw new Error(`Reject failed (${res.status})`);
  return res.json().catch(() => ({}));
}

export async function adminCreatePost(token, payload, { signal } = {}) {
  const res = await requestWithTimeout(
    `${BASE}/api/circle/admin/posts/create/`,
    {
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    { signal }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    console.error('[adminCreatePost] failed payload:', payload);
    console.error('[adminCreatePost] error body:', err);
    const msg = err?.error
      || (err ? Object.entries(err).map(([k, v]) => `${k}: ${[].concat(v).join(', ')}`).join(' | ') : null)
      || `Create failed (${res.status})`;
    throw new Error(msg);
  }
  const data = await res.json();
  // 201 = created, 200 = the idempotency key matched a post an earlier attempt
  // already created. Callers surface the difference so a retry after a timeout
  // reads as "already published" instead of silently looking like a new post.
  return { ...data, _deduplicated: res.status === 200 };
}

export async function adminUpdatePost(token, postId, payload, { signal } = {}) {
  const res = await requestWithTimeout(
    `${BASE}/api/circle/admin/posts/${postId}/edit/`,
    {
      method: 'PATCH',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    { signal }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.error || `Update failed (${res.status})`);
  }
  return res.json();
}

export async function submitPost(token, payload, { signal } = {}) {
  const res = await requestWithTimeout(
    `${BASE}/api/circle/posts/submit/`,
    {
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    { signal }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    const msg = err?.error
      ? (typeof err.error === 'string' ? err.error : JSON.stringify(err.error))
      : `Submit failed (${res.status})`;
    throw new Error(msg);
  }
  const data = await res.json();
  return { ...data, _deduplicated: res.status === 200 };
}

export async function reactToPost(token, postId, reactionType) {
  const res = await fetch(`${BASE}/api/circle/posts/${postId}/react/`, {
    method: 'POST',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ reaction_type: reactionType }),
  });
  if (!res.ok) throw new Error(`React failed (${res.status})`);
  return res.json(); // { post_id, reactions, user_reaction }
}

export async function fetchTopBrands(token, { limit = 10 } = {}) {
  const res = await fetch(`${BASE}/api/circle/top-brands/?limit=${limit}`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(`Failed to load top brands (${res.status})`);
  const data = await res.json();
  return (data.results || []).map((b) => ({
    ...b,
    logo_url: resolveMediaUrl(b.logo_url),
  }));
}
